"use client"

import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Play } from "lucide-react"
import { FullTestModal } from "@/components/testing/full-test-modal"
import type { FlatMapping } from "@/types/ui"
import type { MappingConfig } from "@/types/fbo-api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface FullTestCardProps {
  allMappings: FlatMapping[]
  config: MappingConfig
  primaryEntityId: string
  setPrimaryEntityId: (id: string) => void
  hasInvalidMappings: boolean
}

export function FullTestCard({ 
  allMappings, 
  config, 
  primaryEntityId, 
  setPrimaryEntityId, 
  hasInvalidMappings 
}: FullTestCardProps) {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState(false)

  const handleOpenModal = () => {
    setShowModal(true)
  }

  const isFormValid = primaryEntityId && !hasInvalidMappings

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("testing.fullMappings.title")}</CardTitle>
          <CardDescription>{t("testing.fullMappings.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Exact copy of original form */}
          <div className="space-y-2">
            <Label htmlFor="enhancedTestPrimaryId">{t("testing.fullMappings.primaryEntityId")}</Label>
            <Input
              id="enhancedTestPrimaryId"
              placeholder={t("testing.fullMappings.primaryEntityIdPlaceholder")}
              value={primaryEntityId}
              onChange={(e) => setPrimaryEntityId(e.target.value)}
            />
          </div>
          <Button
            onClick={handleOpenModal}
            disabled={!isFormValid}
            className="w-full"
          >
            <Play className="h-4 w-4 mr-2" />
            {t("testing.fullMappings.run")}
          </Button>
          {hasInvalidMappings && (
            <p className="text-sm text-destructive">{t("testing.fullMappings.invalidWarning")}</p>
          )}
        </CardContent>
      </Card>

      <FullTestModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        allMappings={allMappings}
        config={config}
        primaryEntityId={primaryEntityId}
      />
    </>
  )
}
